import { Injectable } from "@angular/core";
import { select } from "@ngrx/store";
import {
	AuthSelector,
	BankDetailsVerificationStatus,
	CustomerManagementSelector,
	Jurisdiction,
	JurisdictionSelector,
} from "@obg/ngx.core";
import { ignoreNil } from "@obg/rxjs";
import { combineLatest, Observable, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";

import { CommonState } from "../state";

import { IauiVerificationModalState } from "./iaui-verification.model";

@Injectable({
	providedIn: "root",
})
export class IauiVerificationSelector {
	isModalOpen() {
		return (state: CommonState): boolean =>
			state.commonIauIVerification.modalState === IauiVerificationModalState.OPEN;
	}

	isModalClosed() {
		return (state: CommonState): boolean =>
			state.commonIauIVerification.modalState === IauiVerificationModalState.CLOSED;
	}

	isNotificationDismissed() {
		return (state: CommonState): boolean => state.commonIauIVerification.dismissed;
	}

	isVerificationInProgress() {
		return (state: CommonState): boolean => state.commonIauIVerification.isInProgress;
	}

	getSelectedPayment() {
		return (state: CommonState): string | undefined => state.commonIauIVerification.selectedPayment;
	}

	shouldShowVerificatonNotification$() {
		return (state: CommonState) => this.shouldShowNotification(of(state));
	}

	showNotification$() {
		return (state$: Observable<CommonState>): Observable<boolean> =>
			combineLatest([
				state$.pipe(select(this.isModalClosed())),
				state$.pipe(select(AuthSelector.isAuthenticated)),
				state$.pipe(select(JurisdictionSelector.get)),
			]).pipe(
				switchMap(([isModalClosed, isAuth, jurisdiction]) =>
					isModalClosed && isAuth && jurisdiction === Jurisdiction.Iaui
						? this.shouldShowNotification(state$)
						: of(false)
				)
			);
	}

	private shouldShowNotification(state$: Observable<CommonState>): Observable<boolean> {
		return state$.pipe(
			select(CustomerManagementSelector.getCustomer),
			ignoreNil(),
			map((customer) => customer.profile.bankDetailsVerificationStatus),
			switchMap((status) => {
				switch (status) {
					case BankDetailsVerificationStatus.NotChecked:
						return state$.pipe(
							select(this.getSelectedPayment()),
							map((selectedPayment) => !selectedPayment)
						);
					case BankDetailsVerificationStatus.Pending:
					case BankDetailsVerificationStatus.Denied:
						return state$.pipe(
							select(this.isNotificationDismissed()),
							map((isNotificationDismissed) => !isNotificationDismissed)
						);
					default:
						return of(false);
				}
			})
		);
	}
}
